import React, { useEffect, useState } from 'react';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

/**
 * Read-only list of payments recorded against a challan.
 * Props: row, getHistory(subOrderId) -> {success,data}, onClose(),
 *        canReverse (bool), onReverse(paymentId) -> {success,error}
 */
export default function PaymentHistoryModal({ row, getHistory, onClose, canReverse, onReverse }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    const res = await getHistory(row.sub_order_id);
    if (res.success) {
      // The sibling list endpoint answers with an {items,total} envelope, so a
      // shape change here would otherwise blow up in render on items.map.
      setItems(Array.isArray(res.data) ? res.data : (res.data?.items || []));
      // Clear a previous failure — otherwise a failed reversal leaves its banner
      // up over a list that has since reloaded fine.
      setErr(null);
    } else {
      setErr(res.error || 'Failed to load history');
      // Clear the loading placeholder — otherwise `items` stays null and the
      // "Loading…" text renders forever underneath the error banner.
      setItems([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [row?.sub_order_id]);

  const reverse = async (id) => {
    const res = await onReverse(id);
    if (res && res.success === false) { setErr(res.error || 'Failed to reverse'); return; }
    load();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Payments — {row?.challan_number}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-5 text-sm">
          <div className="flex justify-between mb-3">
            <span className="text-gray-500">Total due</span><span className="font-semibold">₹ {money(row?.total_amount)}</span>
          </div>
          <div className="flex justify-between mb-3">
            <span className="text-gray-500">Paid / Outstanding</span>
            <span className="font-semibold">₹ {money(row?.amount_paid)} / <span className="text-red-600">₹ {money(row?.outstanding)}</span></span>
          </div>
          {err && <p className="text-red-600 text-xs mb-2">{err}</p>}
          {!items ? <p className="text-gray-400">Loading…</p> : items.length === 0 ? (
            <p className="text-gray-400">No payments recorded yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b">
                  <th className="text-left py-1">Date</th><th className="text-right">Amount</th>
                  <th className="text-left pl-2">Mode</th><th className="text-left">Ref</th>
                  <th className="text-left">By</th>{canReverse && <th></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-1">{fmt(p.paid_on)}</td>
                    <td className="text-right font-semibold">₹ {money(p.amount)}</td>
                    <td className="pl-2 capitalize">{p.mode || '—'}</td>
                    <td>{p.reference || '—'}</td>
                    <td>{p.recorded_by_name || '—'}</td>
                    {canReverse && (
                      <td className="text-right">
                        <button onClick={() => reverse(p.id)} className="text-red-600 hover:underline">reverse</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex justify-end pt-4">
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-800 text-white">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
